import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

export default function ShippingManagement() {
  const [shippingRates, setShippingRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    governorate: '',
    price: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchShippingRates();
  }, []);

  const fetchShippingRates = async () => {
    try {
      const ratesQuery = collection(db, 'shippingRates');
      const querySnapshot = await getDocs(ratesQuery);
      const ratesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShippingRates(ratesData);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const rateRef = doc(db, 'shippingRates', editing.id);
        await updateDoc(rateRef, formData);
      } else {
        const newRateRef = doc(collection(db, 'shippingRates'));
        await setDoc(newRateRef, formData);
      }
      
      setFormData({ governorate: '', price: 0 });
      setIsAdding(false);
      setEditing(null);
      fetchShippingRates();
    } catch (error) {
      console.error('Error saving shipping rate:', error);
    }
  };

  const handleEdit = (rate) => {
    setEditing(rate);
    setFormData({ governorate: rate.governorate, price: rate.price });
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this shipping rate?')) {
      try {
        await deleteDoc(doc(db, 'shippingRates', id));
        fetchShippingRates();
      } catch (error) {
        console.error('Error deleting shipping rate:', error);
      }
    }
  };

  const handleCancel = () => {
    setFormData({ governorate: '', price: 0 });
    setIsAdding(false);
    setEditing(null);
  };

  if (loading) {
    return (
      <div className="admin-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>Shipping Management</h2>
          <p>Manage shipping rates for different locations</p>
        </div>
        <div style={{display: 'flex', gap: '10px'}}>
           <button className="btn-secondary" onClick={() => navigate('/admin/orders')}>
             <ion-icon name="arrow-back-outline"></ion-icon> Back to Orders
           </button>
           {!isAdding && (
             <button className="btn-primary" onClick={() => setIsAdding(true)}>
               <ion-icon name="add-outline"></ion-icon> Add Rate
             </button>
           )}
        </div>
      </div>

      {isAdding && (
        <div className="admin-card">
          <div className="section-title">
             <ion-icon name={editing ? "create-outline" : "add-circle-outline"}></ion-icon>
             {editing ? 'Edit Shipping Rate' : 'Add New Shipping Rate'}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Governorate Name</label>
                <input
                  type="text"
                  className="custom-input"
                  value={formData.governorate}
                  onChange={(e) => setFormData({...formData, governorate: e.target.value})}
                  placeholder="e.g. Cairo"
                  required
                />
              </div>
              <div className="form-group">
                <label>Shipping Price (EGP)</label>
                <input
                  type="number"
                  className="custom-input"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
            <div className="form-actions" style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {editing ? 'Update Rate' : 'Save Rate'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
        <div className="table-container" style={{border: 'none'}}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Governorate</th>
                <th>Shipping Price</th>
                <th style={{textAlign: 'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shippingRates.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
                    No shipping rates found. Add one above.
                  </td>
                </tr>
              ) : (
                shippingRates.map((rate) => (
                  <tr key={rate.id}>
                    <td style={{fontWeight: 500}}>{rate.governorate}</td>
                    <td>{rate.price.toFixed(2)} EGP</td>
                    <td style={{textAlign: 'right'}}>
                      <button onClick={() => handleEdit(rate)} className="action-btn edit" title="Edit">
                        <ion-icon name="create-outline"></ion-icon>
                      </button>
                      <button onClick={() => handleDelete(rate.id)} className="action-btn delete" title="Delete">
                        <ion-icon name="trash-outline"></ion-icon>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
